import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flag, Trophy, ChevronRight, Star, Clock, Info, Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { PerformanceKPIs } from "@/components/home/PerformanceKPIs";
import { supabase } from "@/integrations/supabase/client";
import { getCountryFlag } from "@/lib/countryFlags";
import { useCountdown } from "@/hooks/useCountdown";
import { format, parseISO } from "date-fns";

const PODIUM_COLORS: Record<number, string> = {
  1: "#FFD700",
  2: "#C0C0C0",
  3: "#CD7F32",
};

const Index = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) return null;

  const displayName = profile?.display_name || "Driver";

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <h1 className="mb-1 text-2xl font-bold md:text-3xl">
        <span className="font-black italic tracking-tight text-primary">SEND IT</span>
      </h1>
      <p className="mb-4 text-muted-foreground">
        Welcome back, <span className="font-medium" style={{ color: profile?.team_colour ? `#${profile.team_colour}` : "#E8002D" }}>{displayName}</span> 👋
      </p>

      <PerformanceKPIs userId={user.id} />

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <CurrentRaceCard userId={user.id} />
        <FavouriteLeaguesCard userId={user.id} />
      </div>
    </div>
  );
};

/* ─── Favourite Leagues Card ─── */
function FavouriteLeaguesCard({ userId }: { userId: string }) {
  const navigate = useNavigate();

  const { data: favourites = [], isLoading } = useQuery({
    queryKey: ["favourite-leagues-home", userId],
    queryFn: async () => {
      // Get favourite league memberships
      const { data: memberships, error: memErr } = await supabase
        .from("league_members")
        .select("league_id, leagues(id, name, emoji)")
        .eq("user_id", userId)
        .eq("is_favourite", true);
      if (memErr) throw memErr;
      if (!memberships?.length) return [];

      // For each favourite league, get standings to find user's rank & tips
      const results = await Promise.all(
        memberships.map(async (m) => {
          const league = m.leagues as any;
          if (!league) return null;
          const { data: standings } = await supabase.rpc("get_league_standings", {
            p_league_id: league.id,
          });
          const hasResults = standings?.some((s: any) => s.total_points > 0) ?? false;
          const userStanding = standings?.find((s: any) => s.user_id === userId);
          const rawRank = standings
            ? standings.findIndex((s: any) => s.user_id === userId) + 1
            : null;
          const rank = hasResults && rawRank && rawRank > 0 ? rawRank : null;
          return {
            id: league.id,
            name: league.name,
            emoji: league.emoji || "🏆",
            rank,
            correctTips: userStanding?.correct_picks ?? 0,
          };
        })
      );
      return results.filter(Boolean) as { id: string; name: string; emoji: string; rank: number | null; correctTips: number }[];
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Favourite Leagues</CardTitle>
        </div>
        <Link to="/leagues" className="text-xs text-muted-foreground hover:text-primary transition-colors">
          View all
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : favourites.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <Star className="h-6 w-6 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Star your favourite leagues to see them here.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate("/leagues")}>
              Go to Leagues
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {favourites.map((league) => {
              const podiumColor = league.rank && league.rank <= 3 ? PODIUM_COLORS[league.rank] : undefined;
              return (
                <div
                  key={league.id}
                  className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/leagues/${league.id}`)}
                >
                  <span className="text-xl flex-shrink-0">{league.emoji}</span>
                  <p className="text-sm font-medium truncate flex-1">{league.name}</p>
                  <span
                    className="text-xs font-bold flex-shrink-0"
                    style={league.rank && league.rank <= 3 ? { color: PODIUM_COLORS[league.rank] } : undefined}
                  >
                    {league.rank ? `P${league.rank}` : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Current Race Card ─── */
function CurrentRaceCard({ userId }: { userId: string }) {
  const navigate = useNavigate();

  const { data: nextRace, isLoading: raceLoading } = useQuery({
    queryKey: ["next-race-home"],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data: inProgress } = await supabase
        .from("races")
        .select("*")
        .neq("status", "completed")
        .neq("status", "cancelled")
        .lte("date_start", now)
        .gte("date_end", now)
        .order("date_start", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (inProgress) return inProgress;

      const { data: upcoming } = await supabase
        .from("races")
        .select("*")
        .neq("status", "cancelled")
        .gt("date_start", now)
        .order("date_start", { ascending: true })
        .limit(1)
        .maybeSingle();
      return upcoming;
    },
  });

  const { data: matchupData } = useQuery({
    queryKey: ["home-matchup-data", nextRace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matchups")
        .select("id, status")
        .eq("race_id", nextRace!.id)
        .in("status", ["published", "resolved"]);
      if (error) throw error;
      const total = data?.length ?? 0;
      const resolvedCount = data?.filter(m => m.status === "resolved").length ?? 0;
      return { total, resolved: resolvedCount > 0 };
    },
    enabled: !!nextRace?.id,
  });

  const { data: userCorrectCount } = useQuery({
    queryKey: ["home-user-correct", nextRace?.id, userId],
    queryFn: async () => {
      const { count: correctPicks } = await supabase
        .from("picks")
        .select("id", { count: "exact", head: true })
        .eq("race_id", nextRace!.id)
        .eq("user_id", userId)
        .eq("is_correct", true);

      const { data: racePick } = await supabase
        .from("race_picks")
        .select("pole_is_correct, sprint_p1_correct, sprint_p2_correct, sprint_p3_correct, predicted_pole_margin, actual_pole_margin")
        .eq("race_id", nextRace!.id)
        .eq("user_id", userId)
        .maybeSingle();

      let correct = correctPicks ?? 0;
      if (racePick?.pole_is_correct) correct++;
      if (racePick?.sprint_p1_correct) correct++;
      if (racePick?.sprint_p2_correct) correct++;
      if (racePick?.sprint_p3_correct) correct++;

      const predicted = racePick?.predicted_pole_margin ?? 0;
      const actual = racePick?.actual_pole_margin ?? 0;
      const marginDelta = Math.abs(predicted - actual) / 1000;

      return { correct, marginDelta };
    },
    enabled: !!nextRace?.id && !!userId && (matchupData?.resolved ?? false),
  });

  const { data: lockSession } = useQuery({
    queryKey: ["home-lock-session", nextRace?.id],
    queryFn: async () => {
      const isSpr = nextRace!.is_sprint_weekend;
      const sessionType = isSpr ? "Sprint Qualifying" : "Qualifying";
      const { data } = await supabase
        .from("sessions")
        .select("date_start")
        .eq("race_id", nextRace!.id)
        .eq("session_type", sessionType)
        .maybeSingle();
      return data?.date_start ?? null;
    },
    enabled: !!nextRace?.id,
  });

  const countdown = useCountdown(lockSession ?? null);

  const { data: userPicks } = useQuery({
    queryKey: ["home-user-picks", nextRace?.id, userId],
    queryFn: async () => {
      const { data: matchupPicks } = await supabase
        .from("picks")
        .select("id")
        .eq("race_id", nextRace!.id)
        .eq("user_id", userId);

      const { data: racePick } = await supabase
        .from("race_picks")
        .select("pole_driver_number, predicted_pole_margin, sprint_p1_driver_number, sprint_p2_driver_number, sprint_p3_driver_number")
        .eq("race_id", nextRace!.id)
        .eq("user_id", userId)
        .maybeSingle();

      let count = matchupPicks?.length ?? 0;
      if (racePick) {
        if (racePick.pole_driver_number) count++;
        if (racePick.sprint_p1_driver_number) count++;
        if (racePick.sprint_p2_driver_number) count++;
        if (racePick.sprint_p3_driver_number) count++;
      }
      return count;
    },
    enabled: !!nextRace?.id && !!userId,
  });

  if (raceLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <Flag className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Current Race Week</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (!nextRace) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <Flag className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Current Race Week</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No upcoming races.</p>
        </CardContent>
      </Card>
    );
  }

  const flag = getCountryFlag(nextRace.country_name);
  const isSprint = nextRace.is_sprint_weekend;
  const matchupCount = matchupData?.total ?? 0;
  const totalRequired = matchupCount + 1 + (isSprint ? 3 : 0);
  const picksMade = userPicks ?? 0;
  const hasMatchups = matchupCount > 0;
  const allComplete = hasMatchups && picksMade >= totalRequired && totalRequired > 0;
  const isLocked = countdown?.locked ?? false;
  const isResolved = matchupData?.resolved ?? false;

  return (
    <Card
      className="cursor-pointer hover:border-primary/40 transition-colors"
      onClick={() => navigate(`/races/${nextRace.id}`)}
    >
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <Flag className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Current Race Week</CardTitle>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">{flag} {nextRace.meeting_name}</h3>
            {isSprint && (
              <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                SPRINT
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Round {nextRace.round_number} · {format(parseISO(nextRace.date_start), "d")}–{format(parseISO(nextRace.date_end), "d MMMM yyyy")}
          </p>
        </div>

        {/* Countdown timer */}
        {countdown && !isResolved ? (
          <div
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium ${
              countdown.locked
                ? "bg-destructive/10 text-destructive"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {countdown.locked ? <Lock className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
            {countdown.text}
          </div>
        ) : null}

        {isSprint && !isResolved && !isLocked && (
          <div className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 font-medium">
            <Info className="h-3.5 w-3.5" />
            All tips lock at the start of Sprint Qualifying
          </div>
        )}

        {!hasMatchups ? (
          <p className="text-sm text-muted-foreground">Matchups not yet published.</p>
        ) : isResolved ? (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-600 hover:bg-green-600 text-white text-xs">
              Race Complete
            </Badge>
            {userCorrectCount && (
              <span className="text-sm text-muted-foreground">
                {userCorrectCount.correct} / {totalRequired} tips · {userCorrectCount.marginDelta.toFixed(3)}s gap
              </span>
            )}
          </div>
        ) : isLocked ? (
          <Badge variant="secondary" className="text-xs flex items-center gap-1 w-fit">
            <Lock className="h-3 w-3" />
            Locked — results pending
          </Badge>
        ) : allComplete ? (
          <Badge className="bg-green-600 hover:bg-green-600 text-white text-xs">
            ✓ Tips complete
          </Badge>
        ) : picksMade === 0 ? (
          <p className="text-sm font-semibold text-primary">
            No tips yet — make your picks!
          </p>
        ) : (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              Your tips: {picksMade} / {totalRequired}
            </p>
            <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
              Tips incomplete — finish your picks
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default Index;
